import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/api/auth';
import router from '@/router';
import { clearAuthState } from './authState';

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem('token'));
  const user = ref<any>(null);
  const loading = ref(false);
  const mustChangePassword = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const selectedInvestorId = computed(() => user.value?.selectedInvestorId || user.value?.investors?.[0]?.id || null);

  // Actions
  const setToken = (newToken: string | null) => {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
  };

  const login = async (email: string, password: string) => {
    loading.value = true;
    try {
      const res = await authApi.login(email, password);
      setToken(res.data.token);
      user.value = res.data.user;
      mustChangePassword.value = res.data.user.mustChangePassword;
      return res;
    } finally {
      loading.value = false;
    }
  };

  const clearSession = () => {
    setToken(null);
    clearAuthState({
      get user() {
        return user.value;
      },
      set user(value) {
        user.value = value;
      },
      get mustChangePassword() {
        return mustChangePassword.value;
      },
      set mustChangePassword(value) {
        mustChangePassword.value = value;
      },
    });
  };

  const logout = () => {
    clearSession();
    return router.replace('/login');
  };

  const fetchUserInfo = async () => {
    if (!token.value) return;
    try {
      const res = await authApi.getCurrentUser();
      user.value = res.data;
      mustChangePassword.value = res.data.mustChangePassword;
    } catch {
      logout();
    }
  };

  const selectInvestor = async (investorId: number) => {
    await authApi.selectInvestor(investorId);
  };

  const forgotPassword = async (email: string, code: string, newPassword: string) => {
    return authApi.forgotPassword(email, code, newPassword);
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    await authApi.changePassword(oldPassword, newPassword);
    logout();
  };

  const checkAccess = async () => {
    const res = await authApi.checkAccess();
    return res.data;
  };

  return {
    token,
    user,
    loading,
    mustChangePassword,
    isAuthenticated,
    selectedInvestorId,
    login,
    clearSession,
    logout,
    fetchUserInfo,
    selectInvestor,
    forgotPassword,
    changePassword,
    checkAccess,
    setToken
  };
});
